import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, DisplayProps, SizeProps } from 'styled-system';
import { backgroundColor as bgColor, borderRadius, display, size, space } from 'styled-system';

import type { BackgroundColorProps, SpaceProps } from '../../../../theme/system-types';
import type { ObjectFitProps } from '../../../../theme/system-utilities';
import { objectFit } from '../../../../theme/system-utilities';
import { createLazyLoadingAttribute, getActiveSlideProps } from '../../../../utils/image';
import ResponsiveImage from '../../../ResponsiveImage';

interface ImageProps extends ComponentProps<typeof ResponsiveImage> {
  isActive: boolean;
  index: number;
  withFixedSize?: boolean;
}

type ResponsiveImageStyles = SizeProps & SpaceProps & BackgroundColorProps & BorderRadiusProps;

type ImageStyles = DisplayProps & SpaceProps & BorderRadiusProps & SizeProps & ObjectFitProps;

const StyledResponsiveImage = styled(ResponsiveImage)<ResponsiveImageStyles>`
  ${size};
  ${space};
  ${bgColor};
  ${borderRadius};
`;

const StyledImage = styled.img<ImageStyles>`
  ${space};
  ${size};
  ${display};
  ${objectFit};
  ${borderRadius};
`;

const responsiveImageStyles: ResponsiveImageStyles = {
  mx: 'auto',
  size: '100%',
  borderRadius: 'medium',
};

const imageStyles: ImageStyles = {
  mx: 'auto',
  size: '100%',
  display: 'block',
  objectFit: 'contain',
  borderRadius: 'medium',
};

const Image = ({ isActive, index, withFixedSize, ...responsiveImageProps }: ImageProps) => {
  if (withFixedSize) {
    return (
      <StyledImage
        {...getActiveSlideProps(isActive)}
        src={responsiveImageProps.src}
        alt=""
        data-testid={`big-image-horizontal-carousel-slide-image-${index}`}
        {...createLazyLoadingAttribute(!isActive)}
        {...imageStyles}
      />
    );
  }

  return (
    <StyledResponsiveImage
      {...responsiveImageStyles}
      {...responsiveImageProps}
      {...getActiveSlideProps(isActive)}
      isLazy={!isActive}
      data-testid={`big-image-horizontal-carousel-slide-image-${index}`}
      alt=""
    />
  );
};

export default Image;
